import { useCallback, useContext, useEffect, useState } from 'react';
import { getApolloContext } from '@apollo/client/react';
import { deserializeMessages, useAiChatContextSafe, type AiMessage } from 'juneau';
import { nowInstant } from '@insio/dates';
import { ChatNudgeId, type ChatNudgeSignal } from '@insio/shared-types';
import { offerChatNudge } from './chat-nudge.api';
import { nudgePart } from './nudge.types';

const NUDGE_IDS: readonly string[] = Object.values(ChatNudgeId);

/**
 * An assistant message carrying only the offer card. Built in the stored shape (ISO time) and
 * read back through juneau, as history is — juneau owns its own Date.
 */
export function nudgeMessage(nudgeId: ChatNudgeId): AiMessage {
  const now = nowInstant();
  const [message] = deserializeMessages([
    {
      id: `nudge-${nudgeId}-${now.epochMilliseconds}`,
      role: 'assistant',
      parts: [nudgePart(nudgeId)],
      createdAt: now.toString()
    }
  ]);
  return message!;
}

/**
 * Reports something the user just did. When the server answers with an offer, its card is
 * appended to the conversation. Every rule (who, how often, whether it applies) is the
 * server's; outside the chat provider this does nothing.
 */
export function useNudgeSignal(): (signal: ChatNudgeSignal) => void {
  const chat = useAiChatContextSafe();
  const { client } = useContext(getApolloContext());
  // The server counts an offer as shown when it answers, so one that arrives during a turn
  // waits for the turn to end rather than being lost.
  const [pending, setPending] = useState<ChatNudgeId | null>(null);

  useEffect(() => {
    if (pending === null || chat === null || chat === undefined || chat.isLoading) return;
    // Juneau has no "append a part" call; reset() with the offer added keeps the
    // conversation. It would abort a streaming turn, hence the wait above.
    chat.reset([...chat.messages, nudgeMessage(pending)]);
    setPending(null);
  }, [pending, chat]);

  return useCallback(
    (signal: ChatNudgeSignal) => {
      if (chat === null || chat === undefined || !client) return;
      void offerChatNudge(client, signal).then(nudgeId => {
        // An id this build does not know has no card to show.
        if (nudgeId === null || !NUDGE_IDS.includes(nudgeId)) return;
        setPending(nudgeId as ChatNudgeId);
      });
    },
    [chat, client]
  );
}
